import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";

import CommonButton from "../../../component/button/Button";
import addCircleIcon from "../../../assets/icons/add-circle.svg";
import {
  formatAmount,
  formatGermanAmount,
  formatPlainAmount,
  formatUpdateNumber,
} from "../../../helper/formatAmount";

import TableComponent from "../../../component/Table/TableComponent";
import editIcon from "../../../assets/icons/Edit.svg";
import deleteIcon from "../../../assets/icons/trash.svg";
import Toast from "../../../component/toastify/Index";
import {
  createUmsatzplanung,
  deleteUmsatzplanung,
  getByIdUmsatzplanung,
  getUmsatzplanungLists,
  loadForderungenOptions,
  updateUmsatzplanung,
} from "../../../services/customer-services/customerUmsatzplanungService";
import GeneratePDF from "../../../component/PDFGenerator";
import UmsatzplanungModal from "./UmsatzplanungModal";

const columns = [
  { field: "kunde", headerName: "Kunde", translationKey: "forderungen_kunde" },
  {
    field: "nettoBetrag",
    headerName: "Netto Betrag",
    translationKey: "forderungen_nettoBetrag",
  },
  {
    field: "bruttoBetrag",
    headerName: "Brutto Betrag",
    translationKey: "forderungen_bruttoBetrag",
  },
  {
    field: "ausVorwoche",
    headerName: "AUS Vorwoche",
    translationKey: "umsatzplanung_aus_vorwoche",
  },
  {
    field: "rechnungsart ",
    headerName: "Rechnungsart",
    translationKey: "umsatzplanung_rechnungsart",
  },
  {
    field: "bemerkung",
    headerName: "Bemerkung",
    translationKey: "umsatzplanung_bemerkung",
  },
  {
    field: "action",
    headerName: "Aktion",
    translationKey: "client_list_action",
  },
];

const Umsatzplanung = ({ shouldDisableDate, selectedWeek, setTotals }) => {
  const { t } = useTranslation();
  const [umsatzplanungs, setUmsatzplanungs] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    kunde: "",
    rechnungsNummer: null,
    datum: null,
    nettoBetrag: "",
    umsatzsteuer: "",
    steuer: "",
    bruttoBetrag: "",
    rechnungsart: "",
    bemerkung: "",
    ausVorwoche: "",
  });

  const rechnungsartOptions = [
    { value: "Rechnung", label: t("umsatzplanung_rechnung") },
    { value: "Abschlagsrechnung", label: t("umsatzplanung_abschlagsrechnung") },
    { value: "Schlussrechnung", label: t("umsatzplanung_schlussrechnung") },
  ];
  const [errors, setErrors] = useState({});
  const [options, setOptions] = useState([]);
  const [forderungenOptions, setForderungenOptions] = useState([]);
  const [debouncedNettoBetrag, setDebouncedNettoBetrag] = useState(
    formData.nettoBetrag || "",
  );
  const [timer, setTimer] = useState(null);
  const parseAmount = (str) => {
    if (!str) return NaN;

    // Remove spaces
    str = str.trim();

    // If comma exists and dot exists
    if (str.includes(",") && str.includes(".")) {
      // German format: "1.234,56"
      return parseFloat(str.replace(/\./g, "").replace(",", "."));
    }
    // Only comma exists: could be German decimal "1234,56"
    else if (str.includes(",") && !str.includes(".")) {
      return parseFloat(str.replace(",", "."));
    }
    // Dot exists or nothing else: English format "1234.56" or "1234"
    else {
      return parseFloat(str);
    }
  };

  useEffect(() => {
    if (!debouncedNettoBetrag || !formData.umsatzsteuer) return;

    const netto = parseAmount(debouncedNettoBetrag);
    const steuerPercent = parseFloat(formData.umsatzsteuer);

    if (!isNaN(netto) && !isNaN(steuerPercent)) {
      const steuerAmount = (netto * steuerPercent) / 100;
      setFormData((prev) => ({
        ...prev,
        steuer: formatAmount(steuerAmount),
        bruttoBetrag: formatAmount(netto + steuerAmount),
      }));
    }
  }, [debouncedNettoBetrag, formData.umsatzsteuer]);

  const getUmsatzplanung = async () => {
    const payload = {
      pageNo,
      perPage,
      year: selectedWeek?.year,
      weekNumber: selectedWeek?.weekNumber,
    };
    const response = await getUmsatzplanungLists(payload);
    if (response?.isSuccess) {
      setUmsatzplanungs([...response.data]);
      setTotalRecords(response.totalRecord);
      const totalNetto = response.data?.reduce(
        (sum, invoice) => sum + Number(invoice?.nettoBetrag),
        0,
      );
      const totalBrutto = response.data?.reduce(
        (sum, invoice) => sum + Number(invoice?.bruttoBetrag),
        0,
      );
      setTotals((prevTotals) => ({
        ...prevTotals,
        umsatzplanungNetto: totalNetto,
        umsatzplanungBrutto: totalBrutto,
      }));
    } else {
      setUmsatzplanungs([]);
      setTotalRecords(0);
      setTotals((prevTotals) => ({
        ...prevTotals,
        umsatzplanungNetto: 0,
        umsatzplanungBrutto: 0,
      }));
    }
  };

  const getforderungenOptions = async () => {
    const response = await loadForderungenOptions();
      if (response?.isSuccess) {
        setForderungenOptions([...response.data]);
        setOptions([...response.data]);
      } else {
        setForderungenOptions([]);
        setOptions([]);
      }
  };

  useEffect(() => {
    getforderungenOptions();
  }, []);

  useEffect(() => {
    getUmsatzplanung();
  }, [pageNo, perPage, selectedWeek]);

  // Handle date change
  const handleDateChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "nettoBetrag") {
      // Immediately set the raw value in the form state to avoid losing user input while typing
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Clear the previous timer if it's still active
      if (timer) {
        clearTimeout(timer);
      }

      // Set a new timer for debouncing the formatted value update
      const newTimer = setTimeout(() => {
        // Format the value only after the debounce delay
        const formattedValue = formatGermanAmount(value);
        setDebouncedNettoBetrag(formattedValue); // Update debounced value after the delay
      }, 500); // 500 ms debounce delay

      setTimer(newTimer);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    const debounceTime = setTimeout(() => {
      setFormData((prev) => ({ ...prev, nettoBetrag: debouncedNettoBetrag }));
    }, 1000);
    return () => clearTimeout(debounceTime);
  }, [debouncedNettoBetrag]);

  const validate = () => {
    const newErrors = {};

    if (!formData.kunde) {
      newErrors.kunde = `${t("forderungen_kunde")} ${t("is_required")}`;
    }

    if (!formData.rechnungsNummer) {
      newErrors.rechnungsNummer = `${t("forderungen_rechnungsNummer")} ${t(
        "is_required",
      )}`;
    }

    if (!formData.datum) {
      newErrors.datum = `${t("umsatzplanung_datum")} ${t("is_required")}`;
    }

    if (!formData.nettoBetrag) {
      newErrors.nettoBetrag = `${t("forderungen_nettoBetrag")} ${t(
        "is_required",
      )}`;
    } else {
      const germanNumber = formData?.nettoBetrag
        ?.replace(/\./g, "")
        ?.replace(/,/g, ".");
      const nettoValue = Number(germanNumber);

      if (isNaN(nettoValue) || !Number.isFinite(nettoValue)) {
        newErrors.nettoBetrag = t("verbindlicheitem_netto_betrag_valid_number");
      } else if (nettoValue <= 0) {
        newErrors.nettoBetrag = `${t("forderungen_nettoBetrag")} ${t(
          "cannot_be_negative",
        )}`;
      }
    }

    if (!formData.umsatzsteuer) {
      newErrors.umsatzsteuer = `${t("forderungen_umsatzsteuer")} ${t(
        "is_required",
      )}`;
    }

    if (!formData.rechnungsart) {
      newErrors.rechnungsart = `${t("umsatzplanung_rechnungsart")} ${t(
        "is_required",
      )}`;
    }

    if (!formData.bemerkung) {
      newErrors.bemerkung = `${t("umsatzplanung_bemerkung")} ${t(
        "is_required",
      )}`;
    }

    if (!formData.ausVorwoche) {
      newErrors.ausVorwoche = `${t("umsatzplanung_aus_vorwoche")} ${t(
        "is_required",
      )}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toCleanNumberString = (value) => {
    if (value == null) return "0";
    const str = String(value);
    const num = Number(str?.replace(/\./g, "")?.replace(",", "."));
    return String(num);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const datum = dayjs(formData?.datum).format("DD-MM-YYYY");
    const nettoBetrag = toCleanNumberString(formatAmount(formData?.nettoBetrag));
    const monthNumber = dayjs(formData?.datum).month() + 1;
    const year = dayjs(formData?.datum).year();
    const steuer = toCleanNumberString(formData?.steuer);
    const bruttoBetrag = toCleanNumberString(formData?.bruttoBetrag);

    const payload = {
      year: selectedWeek?.year,
      weekNumber: selectedWeek?.weekNumber,
      startDate: dayjs(selectedWeek?.weekStart).format("DD-MM-YYYY"),
      endDate: dayjs(selectedWeek?.weekEnd).format("DD-MM-YYYY"),
      umsatzplanung: {
        ...formData,
        datum,
        steuer,
        bruttoBetrag,
        nettoBetrag,
        calendarMonth: monthNumber,
        kundeModel: "Forderungen",
        calendarYear: year,
      },
    };

    let response = [];
    if (editId) {
      response = await updateUmsatzplanung({
        ...payload,
        id: editId,
      });
    } else {
      formData.isManual = true;
      response = await createUmsatzplanung({
        ...payload,
      });
    }
    if (response?.isSuccess) {
      Toast(response?.message, "success");
      setIsModalOpen(false);
      getUmsatzplanung();
      setFormData({
        kunde: "",
        rechnungsNummer: null,
        datum: null,
        nettoBetrag: "",
        umsatzsteuer: "",
        steuer: "",
        bruttoBetrag: "",
        rechnungsart: "",
        bemerkung: "",
        ausVorwoche: "",
      });
      setDebouncedNettoBetrag("");
      setTimer(null);
      setEditId(null);
      console.log(formData.kunde);
      console.log(forderungenOptions);
      setForderungenOptions(forderungenOptions.filter(opt => formData.kunde !== opt.value));
    }
  };

  const handleAdd = () => {
    setIsModalOpen(true);
  };

  const handleEdit = async (editId) => {
    const response = await getByIdUmsatzplanung(editId);
    setFormData({
      ...response.data,
      datum: response.data?.datum ? dayjs(response.data?.datum) : null,
      nettoBetrag: formatUpdateNumber(Number(response.data?.nettoBetrag)),
      bruttoBetrag: formatUpdateNumber(Number(response.data?.bruttoBetrag)),
      steuer: formatUpdateNumber(Number(response.data?.steuer)),
    });
    setDebouncedNettoBetrag(
      formatUpdateNumber(Number(response.data?.nettoBetrag)),
    );
    setEditId(editId);
    setIsModalOpen(true);
    setErrors({});
  };

  const handleDelete = async (deleteId) => {
    const response = await deleteUmsatzplanung(deleteId);
    if (response?.isSuccess) {
      Toast(response?.message, "success");
      getUmsatzplanung();
      getforderungenOptions();
    }
  };

  const totalNetto = umsatzplanungs?.reduce(
    (sum, invoice) => sum + Number(invoice?.nettoBetrag),
    0,
  );
  const totalBrutto = umsatzplanungs?.reduce(
    (sum, invoice) => sum + Number(invoice?.bruttoBetrag),
    0,
  );

  // Generate PDF
  const excludedKeys = [
    "_id",
    "createdAt",
    "updatedAt",
    "isDeleted",
    "__v",
    "company",
    "datum",
    "customers",
    "startDate",
    "steuer",
    "umsatzplanungId",
    "umsatzsteuer",
    "weekNumber",
    "year",
    "endDate",
    "rechnungsNummer",
  ];

  const customLabels = {
    kunde: "Kunde",
    nettoBetrag: "Netto Betrag",
    bruttoBetrag: "Brutto Betrag",
    ausVorwoche: "AUS Vorwoche",
    rechnungsart: "Rechnungsart",
    bemerkung: "Bemerkung",
  };

  const customColumnOrder = [
    "kunde",
    "nettoBetrag",
    "bruttoBetrag",
    "ausVorwoche",
    "rechnungsart",
    "bemerkung",
  ];

  const formatCell = (key, value) => {
    if (["bruttoBetrag", "nettoBetrag"]?.includes(key)) {
      return formatAmount(value) + " €";
    }

    return value || "-";
  };

  return (
    <Box
      sx={{
        paddingTop: 1,
      }}
    >
      <UmsatzplanungModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        handleSubmit={handleSubmit}
        handleChange={handleChange}
        handleDateChange={handleDateChange}
        options={options}
        shouldDisableDate={shouldDisableDate}
        selectedWeek={selectedWeek}
        rechnungsartOptions={rechnungsartOptions}
        t={t}
        debouncedNettoBetrag={debouncedNettoBetrag}
        setDebouncedNettoBetrag={setDebouncedNettoBetrag}
        setTimer={setTimer}
        setEditId={setEditId}
        forderungenOptions={forderungenOptions}
      />
      <Box
        sx={{
          px: 2,
          pt: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start" },
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {t("umsatzplanung_umsatzplanung")}
        </Typography>
        <CommonButton
          text={`${t("umsatzplanung_forderungen")}`}
          onClick={handleAdd}
          icon={
            <img
              src={addCircleIcon}
              alt="addCircleIcon"
              className="button-icon-padding"
            />
          }
          className="add-user-btn button-border-radius"
        />
      </Box>
      <Box
        sx={{
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <TableComponent
          data={umsatzplanungs}
          setPageNo={setPageNo}
          pageNo={pageNo}
          perPage={perPage}
          setPerPage={setPerPage}
          totalRecords={totalRecords}
          columns={columns}
          className={"table-max-height"}
        >
          {umsatzplanungs?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>{row.kunde}</TableCell>
              <TableCell>{formatAmount(row.nettoBetrag) + " €"}</TableCell>
              <TableCell>{formatAmount(row.bruttoBetrag) + " €"}</TableCell>
              <TableCell>
                {row?.ausVorwoche
                  ? t(`common_${row?.ausVorwoche?.toLowerCase()}`)
                  : "-"}
              </TableCell>
              <TableCell>
                {row?.rechnungsart
                  ? t(`umsatzplanung_${row?.rechnungsart?.toLowerCase()}`)
                  : ""}
              </TableCell>
              <TableCell>{row?.bemerkung}</TableCell>
              <TableCell>
                <Typography
                  className="action"
                  classes="flex"
                  alignItems={"center"}
                  display={"flex"}
                >
                  <span onClick={() => handleEdit(row.umsatzplanungId)}>
                    <img src={editIcon} alt="edit icon" className="edit-icon" />
                  </span>
                  <span onClick={() => handleDelete(row.umsatzplanungId)}>
                    <img src={deleteIcon} alt="delete icon" />
                  </span>
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableComponent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: "1rem",
            mt: "1.75rem",
          }}
        >
          <Typography>
            {t("common_sum")} ({t("forderungen_netto")}):{" "}
            {formatAmount(totalNetto) + " €"}
          </Typography>
          <Typography>
            {t("common_sum")} ({t("forderungen_brutto")}):{" "}
            {formatAmount(totalBrutto) + " €"}
          </Typography>
          <GeneratePDF
            data={umsatzplanungs}
            excludedKeys={excludedKeys}
            customLabels={customLabels}
            customColumnOrder={customColumnOrder}
            customRowsPerPage={30}
            formatCell={formatCell}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Umsatzplanung;
